import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle, Leaf } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function downloadBadge(name) {
  const W = 1200, H = 800;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#FAF9F5";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#145C42";
  ctx.fillRect(0, 0, W, 14);
  ctx.fillRect(0, H - 14, W, 14);
  ctx.strokeStyle = "#B08D57";
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 54, W - 80, H - 108);
  ctx.lineWidth = 1;
  ctx.strokeRect(52, 66, W - 104, H - 132);

  ctx.textAlign = "center";
  ctx.fillStyle = "#B08D57";
  ctx.font = "600 26px Georgia, serif";
  ctx.fillText("A L A I N E ' S   B E N C H \u2122", W / 2, 160);

  ctx.fillStyle = "#145C42";
  ctx.font = "bold 64px Georgia, serif";
  ctx.fillText("Founding Product Advisor", W / 2, 300);

  ctx.strokeStyle = "#B08D57";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 180, 345);
  ctx.lineTo(W / 2 + 180, 345);
  ctx.stroke();

  ctx.fillStyle = "#1c1917";
  ctx.font = "italic 44px Georgia, serif";
  ctx.fillText(name || "Founding Advisor", W / 2, 440);

  ctx.fillStyle = "#57534e";
  ctx.font = "24px Georgia, serif";
  ctx.fillText("In recognition of helping shape the future of", W / 2, 520);
  ctx.fillText("food product development", W / 2, 555);

  ctx.fillStyle = "#145C42";
  ctx.font = "bold 34px Georgia, serif";
  ctx.fillText("2026", W / 2, 650);

  const link = document.createElement("a");
  link.download = "alaines-bench-founding-advisor-badge.png";
  link.href = canvas.toDataURL("image/png");
  link.click();
}

const ROLES = [
  "Food Scientist", "R&D Manager", "Product Developer", "QA / Regulatory Professional",
  "Food Entrepreneur / Founder", "Consultant", "Co-packer", "Private Label Manufacturer",
  "Student", "Other",
];
const COMPANY_SIZES = ["Just me", "2–10 employees", "11–50", "51–250", "250+"];
const INDUSTRIES = [
  "Seasonings & Spices", "Sauces & Condiments", "Snacks", "Bakery", "Beverage", "Dairy",
  "Frozen Foods", "Prepared Foods", "Pet Food", "Dietary Supplements", "Contract Manufacturing", "Other",
];
const ADOPTION = ["Immediately", "Within 30 days", "Within 3 months", "Within 6 months", "Not likely"];
const TOOLS = ["Excel", "Google Sheets", "Commercial software", "Nutrition calculator", "Consultant", "Other"];
const FEATURES = [
  "Formulation Builder", "Nutrition Facts", "Ingredient Statements", "Organic Module",
  "Alaine's Compass AI", "Worksheets", "Ingredient Library", "Regulations",
];
const RATINGS = [
  ["rating_ease", "Ease of use"],
  ["rating_accuracy", "Accuracy"],
  ["rating_speed", "Speed"],
  ["rating_design", "Visual design"],
  ["rating_navigation", "Navigation"],
  ["rating_overall", "Overall experience"],
  ["rating_confidence", "Confidence in results"],
  ["rating_time_saved", "Time saved"],
];
const WOULD_USE = ["Definitely", "Probably", "Maybe", "Probably Not", "Definitely Not"];
const PERMISSIONS = ["Yes, with my name", "Yes, anonymously", "No"];
const PRICES = [
  ["price_too_cheap", "Suspiciously cheap", "So low you'd wonder if it really works", "e.g. 10"],
  ["price_good_value", "A great deal", "You'd sign up without hesitating", "e.g. 29"],
  ["price_expensive_ok", "Pricey but fair", "You'd pause, but still pay for the value", "e.g. 79"],
  ["price_too_expensive", "Too expensive", "You'd walk away at this price", "e.g. 150"],
];

function Section({ n, title, children }) {
  return (
    <section className="bg-white border border-stone-200 rounded-xl p-6 md:p-8 space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-amber-700 font-semibold">Part {n}</div>
        <h2 className="font-serif text-xl text-emerald-950 mt-1">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Label({ children, required }) {
  return (
    <label className="block text-sm font-medium text-stone-800 mb-1.5">
      {children} {required && <span className="text-red-700">*</span>}
    </label>
  );
}

function Scale({ value, onChange, max = 5, min = 1, testId }) {
  const nums = [];
  for (let i = min; i <= max; i++) nums.push(i);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {nums.map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          className={`w-9 h-9 rounded-lg border text-sm font-semibold transition-colors ${
            value === i
              ? "bg-emerald-800 text-white border-emerald-800"
              : "bg-white text-stone-700 border-stone-300 hover:border-emerald-600"
          }`}
        >
          {i}
        </button>
      ))}
    </div>
  );
}

export default function AdvisorSurvey() {
  const [f, setF] = useState({
    access_code: "",
    name: "", email: "", role: "", company_size: "", industry: "", current_tools: [], features_used: [],
    rating_ease: null, rating_accuracy: null, rating_speed: null, rating_design: null,
    rating_navigation: null, rating_overall: null, rating_confidence: null, rating_time_saved: null,
    would_use: "", adoption_timeline: "", most_valuable_feature: "", needs_improvement_feature: "",
    indispensable_feature: "", confusing_feedback: "", liked_most: "",
    bugs_encountered: "", bugs_details: "",
    price_too_expensive: "", price_expensive_ok: "", price_good_value: "", price_too_cheap: "",
    testimonial_permission: "", testimonial_helped_me: "", testimonial_one_sentence: "",
    disappointment: null, recommend_nps: null, contact_permission: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) =>
    setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const inputCls = "w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/30 focus:border-emerald-700 bg-white";
  const selectCls = inputCls;
  const textareaCls = inputCls + " min-h-[80px]";

  async function submit(e) {
    e.preventDefault();
    setError("");
    const missing = [];
    if (!f.access_code.trim()) missing.push("access code");
    if (!f.name.trim()) missing.push("name");
    if (!f.email.trim()) missing.push("email");
    if (!f.role) missing.push("your role");
    if (!f.company_size) missing.push("company size");
    RATINGS.forEach(([k, label]) => { if (!f[k]) missing.push(`rating: ${label.toLowerCase()}`); });
    if (!f.would_use) missing.push("would you use it");
    if (!f.most_valuable_feature) missing.push("most valuable feature");
    if (!f.needs_improvement_feature) missing.push("feature needing improvement");
    if (!f.testimonial_permission) missing.push("testimonial permission");
    if (!f.disappointment) missing.push("disappointment score");
    if (f.recommend_nps === null) missing.push("recommendation score");
    if (missing.length) {
      setError(`Please complete: ${missing.slice(0, 4).join(", ")}${missing.length > 4 ? ` and ${missing.length - 4} more` : ""}.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...f };
      ["price_too_expensive", "price_expensive_ok", "price_good_value", "price_too_cheap"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      await axios.post(`${API}/advisor-survey`, payload);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please try again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center p-6">
        <div className="max-w-lg text-center space-y-4">
          <CheckCircle size={56} weight="fill" className="text-emerald-700 mx-auto" />
          <h1 className="font-serif text-3xl text-emerald-950">Thank you!</h1>
          <p className="text-stone-600">
            Your feedback is already helping improve Alaine's Bench. As one of our Founding Product
            Advisors, you'll receive updates showing how your suggestions influence future releases.
          </p>
          <p className="text-stone-600">Thank you for helping build the future of food product development.</p>

          <div className="bg-white border-2 border-amber-700/40 rounded-xl px-8 py-6 text-center space-y-1 shadow-sm">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 font-semibold">Alaine's Bench™</div>
            <div className="font-serif text-2xl text-emerald-950">Founding Product Advisor</div>
            <div className="w-24 h-px bg-amber-700/50 mx-auto my-2" />
            <div className="font-serif italic text-lg text-stone-800">{f.name || "Founding Advisor"}</div>
            <div className="font-semibold text-emerald-900 text-sm pt-1">2026</div>
          </div>
          <button
            type="button"
            onClick={() => downloadBadge(f.name)}
            data-testid="button-download-badge"
            className="bg-emerald-800 hover:bg-emerald-900 text-white font-semibold px-8 py-2.5 rounded-xl text-sm transition-colors"
          >
            Download your badge
          </button>

          <div>
            <Link to="/" className="inline-block text-emerald-800 underline text-sm">Back to Alaine's Bench</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <div className="max-w-3xl mx-auto px-4 py-10 md:py-14 space-y-6">
        <header className="text-center space-y-3 mb-8">
          <div className="inline-flex items-center gap-2 text-emerald-800">
            <Leaf size={22} weight="fill" />
            <span className="text-xs uppercase tracking-[0.3em] font-semibold">Alaine's Bench™</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-emerald-950">Founding Product Advisor Survey</h1>
          <p className="text-stone-600 max-w-xl mx-auto text-sm md:text-base">
            Thank you for participating as one of our Founding Product Advisors. Your feedback will
            directly influence the future of Alaine's Bench. This takes about 10–15 minutes.
          </p>
        </header>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm" data-testid="survey-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          <section className="bg-emerald-900 rounded-xl p-6 md:p-8">
            <Label required>
              <span className="text-white">Access code from your invitation</span>
            </Label>
            <input
              className={inputCls + " md:max-w-xs tracking-widest uppercase"}
              value={f.access_code}
              onChange={(e) => set("access_code", e.target.value)}
              placeholder="Enter your code"
              data-testid="input-access-code"
            />
            <p className="text-emerald-100/80 text-xs mt-2">
              This survey is for invited Founding Product Advisors. Your invitation includes the code.
            </p>
          </section>

          <Section n={1} title="About you">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label required>Your name</Label>
                <input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="input-name" />
              </div>
              <div>
                <Label required>Email</Label>
                <input type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
              </div>
              <div>
                <Label required>Which best describes your role?</Label>
                <select className={selectCls} value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="select-role">
                  <option value="">Select…</option>
                  {ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label required>Company size</Label>
                <select className={selectCls} value={f.company_size} onChange={(e) => set("company_size", e.target.value)} data-testid="select-company-size">
                  <option value="">Select…</option>
                  {COMPANY_SIZES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label>What industry best describes your organization?</Label>
                <select className={selectCls} value={f.industry} onChange={(e) => set("industry", e.target.value)} data-testid="select-industry">
                  <option value="">Select…</option>
                  {INDUSTRIES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>How do you currently manage formulations? (check all that apply)</Label>
              <div className="flex flex-wrap gap-2">
                {TOOLS.map((t) => (
                  <button key={t} type="button" onClick={() => toggle("current_tools", t)}
                    className={`px-3 py-1.5 rounded-full border text-sm ${f.current_tools.includes(t) ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-700 border-stone-300"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          <Section n={2} title="Which features did you use?">
            <div className="flex flex-wrap gap-2">
              {FEATURES.map((t) => (
                <button key={t} type="button" onClick={() => toggle("features_used", t)}
                  className={`px-3 py-1.5 rounded-full border text-sm ${f.features_used.includes(t) ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-700 border-stone-300"}`}>
                  {t}
                </button>
              ))}
            </div>
          </Section>

          <Section n={3} title="Rate your experience (1 = poor, 5 = excellent)">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
              {RATINGS.map(([k, label]) => (
                <div key={k}>
                  <Label required>{label}</Label>
                  <Scale value={f[k]} onChange={(v) => set(k, v)} testId={`scale-${k}`} />
                </div>
              ))}
            </div>
          </Section>

          <Section n={4} title="Product value">
            <div>
              <Label required>Would you use Alaine's Bench in your work?</Label>
              <select className={selectCls} value={f.would_use} onChange={(e) => set("would_use", e.target.value)} data-testid="select-would-use">
                <option value="">Select…</option>
                {WOULD_USE.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label>How soon could you realistically see yourself using Alaine's Bench?</Label>
              <select className={selectCls} value={f.adoption_timeline} onChange={(e) => set("adoption_timeline", e.target.value)} data-testid="select-adoption">
                <option value="">Select…</option>
                {ADOPTION.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label required>Which feature provided the most value?</Label>
                <select className={selectCls} value={f.most_valuable_feature} onChange={(e) => set("most_valuable_feature", e.target.value)} data-testid="select-most-valuable">
                  <option value="">Select…</option>
                  {FEATURES.concat(["Other"]).map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label required>Which feature needs the most improvement?</Label>
                <select className={selectCls} value={f.needs_improvement_feature} onChange={(e) => set("needs_improvement_feature", e.target.value)} data-testid="select-needs-improvement">
                  <option value="">Select…</option>
                  {FEATURES.concat(["Other"]).map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>What feature would make this indispensable for you?</Label>
              <textarea className={textareaCls} value={f.indispensable_feature} onChange={(e) => set("indispensable_feature", e.target.value)} />
            </div>
            <div>
              <Label>What was confusing, or took longer than you expected?</Label>
              <textarea className={textareaCls} value={f.confusing_feedback} onChange={(e) => set("confusing_feedback", e.target.value)} />
            </div>
            <div>
              <Label>What did you like most?</Label>
              <textarea className={textareaCls} value={f.liked_most} onChange={(e) => set("liked_most", e.target.value)} />
            </div>
            <div>
              <Label>Did you encounter any bugs or unexpected behavior?</Label>
              <div className="flex gap-2" data-testid="bugs-encountered">
                {["No", "Yes"].map((v) => (
                  <button key={v} type="button" onClick={() => set("bugs_encountered", v)}
                    className={`px-5 py-1.5 rounded-full border text-sm ${f.bugs_encountered === v ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-700 border-stone-300"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            {f.bugs_encountered === "Yes" && (
              <div>
                <Label>Please describe what happened — every detail helps.</Label>
                <textarea className={textareaCls} value={f.bugs_details} onChange={(e) => set("bugs_details", e.target.value)} data-testid="input-bugs-details" />
              </div>
            )}
          </Section>

          <Section n={5} title="Pricing">
            <p className="text-sm text-stone-600 -mt-2">
              Imagine Alaine's Bench had a monthly subscription. For each line below, type the
              dollar amount that fits the description. There are no right answers — your gut
              feeling is exactly what we're looking for. (All four are optional.)
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {PRICES.map(([k, label, hint, placeholder]) => (
                <div key={k}>
                  <Label>{label}</Label>
                  <p className="text-xs text-stone-500 mb-1.5 -mt-1">{hint}</p>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm">$</span>
                    <input type="number" min="0" step="1" className={inputCls + " pl-7"} value={f[k]}
                      placeholder={placeholder}
                      onChange={(e) => set(k, e.target.value)} data-testid={`input-${k}`} />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section n={6} title="Testimonial">
            <div>
              <Label required>May we use your feedback publicly (website, presentations)?</Label>
              <select className={selectCls} value={f.testimonial_permission} onChange={(e) => set("testimonial_permission", e.target.value)} data-testid="select-permission">
                <option value="">Select…</option>
                {PERMISSIONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label>Complete this sentence: "Alaine's Bench helped me…"</Label>
              <textarea className={textareaCls} value={f.testimonial_helped_me} onChange={(e) => set("testimonial_helped_me", e.target.value)} />
            </div>
            <div>
              <Label>Describe Alaine's Bench in one sentence.</Label>
              <textarea className={textareaCls} value={f.testimonial_one_sentence} onChange={(e) => set("testimonial_one_sentence", e.target.value)} />
            </div>
          </Section>

          <Section n={7} title="Two final questions">
            <div>
              <Label required>If Alaine's Bench disappeared tomorrow, how disappointed would you be? (1 = not at all, 10 = very)</Label>
              <Scale value={f.disappointment} onChange={(v) => set("disappointment", v)} min={1} max={10} testId="scale-disappointment" />
            </div>
            <div>
              <Label required>How likely are you to recommend Alaine's Bench to a colleague? (0–10)</Label>
              <Scale value={f.recommend_nps} onChange={(v) => set("recommend_nps", v)} min={0} max={10} testId="scale-nps" />
            </div>
            <div>
              <Label>May Courtney contact you to discuss your feedback?</Label>
              <div className="flex gap-2" data-testid="contact-permission">
                {["Yes", "No"].map((v) => (
                  <button key={v} type="button" onClick={() => set("contact_permission", v)}
                    className={`px-5 py-1.5 rounded-full border text-sm ${f.contact_permission === v ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-700 border-stone-300"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          <div className="text-center pt-2 pb-10">
            <button type="submit" disabled={submitting} data-testid="button-submit-survey"
              className="bg-emerald-800 hover:bg-emerald-900 disabled:opacity-60 text-white font-semibold px-10 py-3 rounded-xl text-base transition-colors">
              {submitting ? "Submitting…" : "Submit survey"}
            </button>
            <p className="text-xs text-stone-500 mt-3">Your answers are stored privately and shared only as you permit above.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
